import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
/** Cache product JSON by product URL for immediate variant lookups (inventory, etc.) */
const productJsonCache = new Map();

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Fetches product JSON and returns variant by ID. Uses cache for instant repeat lookups.
   * Enables immediate inventory/price updates when color swatch is clicked (before section fetch).
   * @param {string} productUrl - e.g. /products/foo
   * @param {string|number} variantId - Variant ID
   * @returns {Promise<Object|null>} Variant object or null
   */
  static async getProductJson(productUrl) {
    if (!productUrl) return null;
    const baseUrl = (productUrl || '').split('?')[0].replace(/\/$/, '');
    const jsonUrl = baseUrl ? `${baseUrl}.js` : '';
    if (!jsonUrl) return null;
    let product = productJsonCache.get(jsonUrl);
    if (!product) {
      try {
        const res = await fetch(jsonUrl);
        if (!res.ok) return null;
        product = await res.json();
        productJsonCache.set(jsonUrl, product);
      } catch {
        return null;
      }
    }
    return product;
  }

  static async getVariantFromProductJson(productUrl, variantId) {
    const product = await VariantPicker.getProductJson(productUrl);
    if (!product?.variants) return null;
    return product.variants.find((v) => String(v.id) === String(variantId)) || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Add click event listener for radio buttons to ensure active class is applied
    this.addEventListener('click', (event) => {
      if (event.target.type === 'radio') {
        this.updateSelectedOption(event.target);
      }
    });

    // Ensure first available (non-disabled) option is selected when a disabled one is currently selected
    requestAnimationFrame(() => this.ensureFirstAvailableSelected());

    // Preload product JSON for instant inventory/price updates when color swatch is clicked
    const productUrl = this.dataset.productUrl;
    if (productUrl && /\/products\//.test(productUrl)) {
      const baseUrl = productUrl.split('?')[0].replace(/\/$/, '');
      if (baseUrl && !productJsonCache.has(baseUrl + '.js')) {
        fetch(baseUrl + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product) productJsonCache.set(baseUrl + '.js', product);
          })
          .catch(() => {});
      }
    }

    // Preload variant featured image on color swatch hover to speed up gallery update on click
    this.#setupColorSwatchImagePreload();
  }

  /**
   * Preloads variant featured images when hovering over color swatches.
   * Improves perceived load time when user clicks a swatch.
   */
  #setupColorSwatchImagePreload() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl || !/\/products\//.test(productUrl)) return;

    this.addEventListener(
      'pointerover',
      (e) => {
        const target = e.target?.closest?.('label.color-swatch, label.sunice-variant-option__button-label.color-swatch, label.variant-option__button-label--has-swatch');
        if (!target) return;
        const input = target.querySelector?.('input[data-variant-id]');
        const variantId = input?.dataset?.variantId;
        if (!variantId) return;

        VariantPicker.getProductJson(productUrl).then((product) => {
          if (!product?.variants) return;
          const variant = product.variants.find((v) => String(v.id) === String(variantId));
          const featuredMedia = variant?.featured_media;
          const imgSrc = featuredMedia?.preview_image?.src || featuredMedia?.src;
          if (imgSrc) {
            const img = new Image();
            img.src = imgSrc;
          }
        });
      },
      { capture: true, passive: true }
    );
  }

  /**
   * If the selected option in any group is disabled but an available option exists,
   * select the first available option and trigger a variant update.
   */
  ensureFirstAvailableSelected() {
    if (this.dataset.isGiftCard === 'true') return;
    const optionContainers = this.querySelectorAll('.variant-option');
    for (const container of optionContainers) {
      let selected = container.querySelector('input[type="radio"]:checked');
      if (!selected) {
        const sel = container.querySelector('select');
        if (sel) selected = sel.options[sel.selectedIndex];
      }
      if (!selected || selected.disabled || selected.dataset?.optionAvailable === 'false') {
        const firstAvailable = container.querySelector('input[type="radio"]:not([disabled])[data-option-available="true"]');
        if (firstAvailable) {
          firstAvailable.checked = true;
          this.updateSelectedOption(firstAvailable);
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const sel = container.querySelector('select');
        if (sel) {
          const availableOpt = Array.from(sel.options).find(o => !o.disabled);
          if (availableOpt) {
            sel.value = availableOpt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    
    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
        variantId: selectedOption.dataset.variantId || null,
      })
    );

    const variantId = selectedOption.dataset.variantId || null;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, loadsNewProduct), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Check if this is a gift card product
    const isGiftCard = this.dataset.isGiftCard === 'true';

    if (target instanceof HTMLInputElement) {
      target.checked = true;
      
      // Add active class to the label for Sunice variant pickers
      const label = target.closest('label');
      if (label) {
        const fieldset = target.closest('fieldset');
        // For color swatches (Core or Seasonal), clear active from ALL color swatches in picker
        // so that when switching between Core and Seasonal, the other section loses active state
        if (label.classList.contains('color-swatch')) {
          this.querySelectorAll('label.color-swatch').forEach((l) => {
            l.classList.remove('active');
            l.removeAttribute('data-active');
          });
        } else if (fieldset) {
          const allLabels = fieldset.querySelectorAll('label');
          allLabels.forEach(l => {
            l.classList.remove('active');
            l.removeAttribute('data-active');
          });
        }
        
        // Add active class to the clicked label
        label.classList.add('active');
        
        // For color swatches, also set data-active attribute
        if (label.classList.contains('color-swatch')) {
          label.setAttribute('data-active', 'true');
        }
        
        // For gift cards, ensure the clicked variant is always marked as available
        if (isGiftCard) {
          label.setAttribute('data-option-available', 'true');
          target.setAttribute('data-option-available', 'true');
          // Remove any unavailable classes
          label.classList.remove('unavailable');
          label.removeAttribute('data-unavailable');
          
          // Update the price in the legend
          const fieldset = target.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend');
            if (legend) {
              const variantPrice = target.dataset.variantPrice;
              if (variantPrice) {
                // Find or create the selected value span
                let priceSpan = legend.querySelector('.variant-option__selected-value');
                if (!priceSpan) {
                  priceSpan = document.createElement('span');
                  priceSpan.className = 'variant-option__selected-value';
                  legend.appendChild(priceSpan);
                }
                priceSpan.textContent = variantPrice;
              }
            }
          }
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Gets selected option value IDs for the request.
   * When color changes, includes size too if the current size is still available for the new color
   * (so the server preserves the size selection instead of resetting to first available).
   * @param {HTMLElement} changedOption - The option element that was changed (radio input or select option).
   * @returns {string[]} Option value IDs.
   */
  getSelectedOptionsValuesUpToChanged(changedOption) {
    const optionContainers = Array.from(this.querySelectorAll('.variant-option'));
    const changedContainer = changedOption.closest('.variant-option');
    if (!changedContainer) return this.selectedOptionsValues;
    const changedIndex = optionContainers.indexOf(changedContainer);
    if (changedIndex < 0) return this.selectedOptionsValues;

    const values = [];
    for (let i = 0; i <= changedIndex && i < optionContainers.length; i++) {
      const container = optionContainers[i];
      let checked = container.querySelector('input[type="radio"]:checked');
      if (!checked) {
        const sel = container.querySelector('select');
        if (sel) checked = sel.options[sel.selectedIndex];
      }
      if (checked && checked.dataset?.optionValueId) values.push(checked.dataset.optionValueId);
    }

    // When a color option changes (not size), include size if it's still available for the new color
    const changedLegend = changedContainer?.querySelector('legend');
    const isColorChange =
      changedLegend &&
      /Color|Colour|Core|Seasonal/i.test(changedLegend.textContent || '');
    const sizeContainer = optionContainers.find((c) => {
      const leg = c.querySelector('legend');
      return leg && /^Size:/i.test((leg.textContent || '').trim());
    });
    if (isColorChange && sizeContainer) {
      const sizeChecked = sizeContainer.querySelector('input[type="radio"]:checked');
      const newColorValue =
        (changedOption instanceof HTMLInputElement || changedOption instanceof HTMLOptionElement)
          ? changedOption.value
          : (changedOption.textContent || '').trim();
      const currentSizeValue =
        sizeChecked instanceof HTMLInputElement ? sizeChecked.value : null;
      if (newColorValue && currentSizeValue) {
        const productData = this.#getProductVariantsData();
        if (productData && this.#isSizeAvailableForColor(productData, newColorValue, currentSizeValue)) {
          const sizeOptionValueId =
            sizeChecked instanceof HTMLInputElement ? sizeChecked.dataset?.optionValueId : null;
          if (sizeOptionValueId && !values.includes(sizeOptionValueId)) {
            values.push(sizeOptionValueId);
          }
        }
      }
    }

    return values.length > 0 ? values : this.selectedOptionsValues;
  }

  /**
   * Gets product variants data from the product-variants-data script (PDP only).
   * @returns {{ variants: Array, options: Array } | null}
   */
  #getProductVariantsData() {
    const script = this.querySelector('.product-variants-data');
    if (!(script instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }

  /**
   * Checks if the given size is available for the given color.
   * @param {{ variants: Array, options: Array }} productData
   * @param {string} colorValue - Color option value (e.g. "Black")
   * @param {string} sizeValue - Size option value (e.g. "M")
   * @returns {boolean}
   */
  #isSizeAvailableForColor(productData, colorValue, sizeValue) {
    const variants = productData?.variants || [];
    const options = productData?.options || [];
    const colorOpt = options.find((o) => o?.name === 'Color' || o?.name === 'Colour');
    const sizeOpt = options.find((o) => o?.name === 'Size');
    if (!colorOpt || !sizeOpt) return false;
    const colorPos = colorOpt.position - 1;
    const sizePos = sizeOpt.position - 1;
    const norm = (v) => (v || '').toString().toLowerCase().trim();
    const colorNorm = norm(colorValue);
    const sizeNorm = norm(sizeValue);
    return variants.some(
      (v) =>
        norm(v.options?.[colorPos]) === colorNorm &&
        norm(v.options?.[sizePos]) === sizeNorm &&
        v.available === true
    );
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {boolean} [loadsNewProduct] - If loading a new product (combined listing).
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, loadsNewProduct = false, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      const optionsToSend = this.getSelectedOptionsValuesUpToChanged(selectedOption);
      params.push(`option_values=${optionsToSend.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // On PDP, fetch only the main section to reduce payload and speed up media gallery update (unless loading a different product)
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');
    if (isOnProductPage && !loadsNewProduct && params.length > 0) {
      const sectionId = this.dataset.sectionId || 'main';
      params.push(`section_id=${sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Preserve color swatch backgrounds (file-based images) before morph so they are not lost
    // when only size changes - server may re-render with grey/product image; we keep the originals
    const swatchBackgroundsByValue = new Map();
    this.querySelectorAll('.color-swatch .swatch, .sunice-variant-option__button-label.color-swatch .swatch').forEach((swatchEl) => {
      const label = swatchEl.closest('label');
      const input = label?.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement && label) {
        const value = input.value || input.dataset.optionValueId || '';
        const bg = swatchEl.style.getPropertyValue('--swatch-background')?.trim() ||
          swatchEl.dataset.swatchBackground ||
          getComputedStyle(swatchEl).getPropertyValue('--swatch-background')?.trim();
        if (value && bg && bg !== 'rgb(200, 200, 200)' && bg !== '') {
          swatchBackgroundsByValue.set(value, bg);
        }
      }
    });

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Restore color swatch backgrounds so file-based images stay (not grey/product image)
    if (swatchBackgroundsByValue.size > 0) {
      this.querySelectorAll('.color-swatch .swatch, .sunice-variant-option__button-label.color-swatch .swatch').forEach((swatchEl) => {
        const label = swatchEl.closest('label');
        const input = label?.querySelector('input[type="radio"]');
        if (input instanceof HTMLInputElement) {
          const value = input.value || input.dataset.optionValueId || '';
          const savedBg = swatchBackgroundsByValue.get(value);
          if (savedBg) {
            swatchEl.style.setProperty('--swatch-background', savedBg);
            swatchEl.dataset.swatchBackground = savedBg;
          }
        }
      });
    }

    // Check if this is a gift card product
    const isGiftCard = this.dataset.isGiftCard === 'true';

    // CRITICAL: Do NOT modify data-option-available based on background color
    // The Liquid template already sets the correct data-option-available attributes
    // based on actual variant availability. We should trust those values.
    // Only handle gift cards which need special treatment
    if (isGiftCard) {
      // For gift cards, ensure all variants are marked as available
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        // Skip facet swatches - never modify them (desktop or mobile drawer)
        if (input.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
          return;
        }
        
        const label = input.closest('label');
        if (label && input) {
          label.setAttribute('data-option-available', 'true');
          input.setAttribute('data-option-available', 'true');
        }
      });
      
      // Update the price in the legend for gift cards
      this.querySelectorAll('input[type="radio"]:checked').forEach((checkedInput) => {
        const fieldset = checkedInput.closest('fieldset');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const variantPrice = checkedInput.dataset.variantPrice;
            if (variantPrice) {
              // Find or create the selected value span
              let priceSpan = legend.querySelector('.variant-option__selected-value');
              if (!priceSpan) {
                priceSpan = document.createElement('span');
                priceSpan.className = 'variant-option__selected-value';
                legend.appendChild(priceSpan);
              }
              priceSpan.textContent = variantPrice;
            }
          }
        }
      });
    }
    
    // For non-gift cards, the data-option-available attributes are already correctly set by Liquid
    // based on actual variant availability. We should NOT override them.

    // Ensure first available option is selected if a disabled one is currently selected
    requestAnimationFrame(() => this.ensureFirstAvailableSelected());

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
